// Small shared UI primitives for the Agency console sections. Tailwind + the
// `brand` accent (var(--ss-accent)). Kept dependency-free (no chart/UI libs).
import type { ReactNode } from 'react';

// ── Layout ─────────────────────────────────────────────────────────
export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-card border border-th-border bg-surface shadow-card ${className}`}>{children}</div>
  );
}

export function SectionHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="mb-5 flex items-start justify-between gap-4">
      <div>
        <h2 className="font-display text-base font-semibold text-ink">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// ── Feedback states ────────────────────────────────────────────────
export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 py-16 text-sm text-muted">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-th-border border-t-brand" />
      {label}
    </div>
  );
}

export function ErrorNote({ error }: { error: string }) {
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>
  );
}

export function EmptyNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-card border border-dashed border-th-border bg-surface p-10 text-center text-sm text-muted">
      {children}
    </div>
  );
}

// ── Buttons ────────────────────────────────────────────────────────
export function Button({
  children,
  onClick,
  variant = 'primary',
  type = 'button',
  disabled,
  className = '',
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  type?: 'button' | 'submit';
  disabled?: boolean;
  className?: string;
  /** Native tooltip. Mainly for disabled buttons, where the user needs to know WHY it is disabled —
   *  a dead control with no explanation reads as a bug. */
  title?: string;
}) {
  const base =
    'inline-flex items-center justify-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50';
  const styles: Record<string, string> = {
    primary: 'bg-brand text-brand-fg hover:opacity-90',
    secondary: 'bg-surface text-ink ring-1 ring-th-border hover:bg-canvas',
    ghost: 'text-muted hover:bg-canvas',
    danger: 'bg-rose-600 text-white hover:bg-rose-700',
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} title={title} className={`${base} ${styles[variant]} ${className}`}>
      {children}
    </button>
  );
}

// ── Toggle ─────────────────────────────────────────────────────────
export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: string;
  disabled?: boolean;
}) {
  return (
    <label className={`inline-flex items-center gap-2 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 shrink-0 rounded-full transition ${checked ? 'bg-brand' : 'bg-th-border'}`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
            checked ? 'left-[22px]' : 'left-0.5'
          }`}
        />
      </button>
      {label && <span className="text-sm text-ink">{label}</span>}
    </label>
  );
}

// ── Chip ───────────────────────────────────────────────────────────
// A pill that can take an explicit hex color (from ticket meta) and derive a soft bg.
export function Chip({ label, color, muted }: { label: string; color?: string | null; muted?: boolean }) {
  if (color) {
    return (
      <span
        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
        style={{ backgroundColor: `${color}1a`, color }}
      >
        {label}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
        muted ? 'bg-canvas text-muted' : 'bg-canvas text-ink'
      }`}
    >
      {label}
    </span>
  );
}

// ── Stat tile ──────────────────────────────────────────────────────
export function StatTile({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <Card className="p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-1 font-display text-2xl font-semibold text-ink">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted">{hint}</div>}
    </Card>
  );
}

// ── Horizontal bar list (CSS bars, no chart lib) ───────────────────
export function BarList({
  items,
  emptyLabel = 'No data yet.',
}: {
  items: Array<{ label: string; value: number }>;
  emptyLabel?: string;
}) {
  if (!items.length) return <p className="py-6 text-sm text-muted">{emptyLabel}</p>;
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="space-y-2.5">
      {items.map((it, i) => (
        <div key={`${it.label}-${i}`} className="flex items-center gap-3">
          <div className="w-32 shrink-0 truncate text-sm text-muted" title={it.label}>
            {it.label}
          </div>
          <div className="relative h-6 flex-1 overflow-hidden rounded bg-canvas">
            <div
              className="absolute inset-y-0 left-0 rounded bg-brand/80"
              style={{ width: `${(it.value / max) * 100}%` }}
            />
          </div>
          <div className="w-10 shrink-0 text-right text-sm font-medium tabular-nums text-ink">
            {it.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Select ─────────────────────────────────────────────────────────
export function Select({
  value,
  onChange,
  children,
  className = '',
}: {
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`rounded-lg border border-th-border bg-surface px-3 py-1.5 text-sm text-ink outline-none focus:border-brand focus:ring-1 focus:ring-brand ${className}`}
    >
      {children}
    </select>
  );
}
